import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { AutomaticCorrectionService } from '../../../services/automatic-correction.service';
import { AutomaticCorrectionCheckDto } from '../../../models/api/automaticCorrectionCheckDto';
import { AutomaticCorrectionResultDto } from '../../../models/api/automaticCorrectionResultDto';
import { AutomaticCorrectionCheckType } from '../../../models/enums/automaticCorrectionCheckType.enum';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { AutomaticCorrectionRowDeletionResultDto } from '../../../models/api/automaticCorrectionRowDeletionResultDto';

interface CheckRow extends AutomaticCorrectionCheckDto {
  refreshing: boolean;
  applying: boolean;
  exporting: boolean;
}

interface DetailsState {
  results: AutomaticCorrectionResultDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  loading: boolean;
}

@Component({
  selector: 'app-automatic-correction',
  imports: [
    CommonModule,
    MaterialModule,
    NgxSpinnerModule,
    RouterLink
  ],
  templateUrl: './automatic-correction.component.html',
  styleUrl: './automatic-correction.component.scss'
})
export class AutomaticCorrectionComponent implements OnInit {
  private service = inject(AutomaticCorrectionService);
  private spinnerService = inject(NgxSpinnerService);
  private toast = inject(HotToastService);
  private confirmationService = inject(ConfirmationDialogService);

  checks: CheckRow[] = [];
  loaded = false;
  refreshingAll = false;

  expandedCheck: AutomaticCorrectionCheckType | null = null;
  details: Record<number, DetailsState> = {};

  readonly pageSizeOptions = [5, 10, 25, 50];
  readonly displayedColumns = ['label', 'count', 'actions'];
  private readonly baseDetailColumns = ['name', 'company', 'country', 'year', 'info'];

  /** Ids of the rows currently being deleted, so each button disables on its own. */
  deletingRows = new Set<string>();

  /** The trailing Delete column only exists for checks that can act on one record. */
  detailColumnsFor(row: CheckRow): string[] {
    return row.supportsRowDeletion
      ? [...this.baseDetailColumns, 'rowActions']
      : this.baseDetailColumns;
  }

  ngOnInit(): void {
    this.loadChecks();
  }

  loadChecks(): void {
    this.spinnerService.show();
    this.refreshingAll = true;

    this.service.getChecks().subscribe({
      next: (checks) => {
        this.checks = checks.map(c => ({ ...c, refreshing: false, applying: false, exporting: false }));
        this.loaded = true;
        this.refreshingAll = false;
        this.spinnerService.hide();
      },
      error: () => {
        this.refreshingAll = false;
        this.spinnerService.hide();
        this.toast.error('Error loading automatic correction checks');
      }
    });
  }

  totalIssues(): number {
    return this.checks.reduce((sum, c) => sum + c.count, 0);
  }

  cleanChecks(): number {
    return this.checks.filter(c => c.count === 0).length;
  }

  isExpanded(checkType: AutomaticCorrectionCheckType): boolean {
    return this.expandedCheck === checkType;
  }

  toggleDetails(row: CheckRow): void {
    if (this.expandedCheck === row.checkType) {
      this.expandedCheck = null;
      return;
    }
    this.expandedCheck = row.checkType;
    if (!this.details[row.checkType]) {
      this.loadDetails(row.checkType, 1, 10);
    }
  }

  loadDetails(checkType: AutomaticCorrectionCheckType, page: number, pageSize: number): void {
    const existing = this.details[checkType];
    this.details[checkType] = {
      results: existing?.results ?? [],
      totalCount: existing?.totalCount ?? 0,
      page,
      pageSize,
      loading: true
    };

    this.service.getDetails(checkType, page, pageSize).subscribe({
      next: (response) => {
        this.details[checkType] = {
          results: response.data,
          totalCount: response.totalCount,
          page,
          pageSize,
          loading: false
        };
      },
      error: () => {
        this.details[checkType] = {
          ...this.details[checkType],
          loading: false
        };
        this.toast.error('Error loading details');
      }
    });
  }

  onDetailsPage(checkType: AutomaticCorrectionCheckType, event: PageEvent): void {
    this.loadDetails(checkType, event.pageIndex + 1, event.pageSize);
  }

  refreshOne(row: CheckRow): void {
    row.refreshing = true;
    this.service.getChecks().subscribe({
      next: (checks) => {
        const fresh = checks.find(c => c.checkType === row.checkType);
        if (fresh) {
          row.count = fresh.count;
        }
        row.refreshing = false;
      },
      error: () => {
        row.refreshing = false;
        this.toast.error('Error refreshing count');
      }
    });
  }

  exportCsv(row: CheckRow): void {
    if (row.count === 0) {
      this.toast.info('Nothing to export — this check found no records.');
      return;
    }

    row.exporting = true;
    this.service.exportCsv(row.checkType).subscribe({
      next: (response) => {
        this.downloadCsv(response.body!, this.getFilename(response, `automatic_correction_${AutomaticCorrectionCheckType[row.checkType]}`));
        row.exporting = false;
      },
      error: () => {
        row.exporting = false;
        this.toast.error('Error exporting CSV');
      }
    });
  }

  applyCorrection(row: CheckRow): void {
    if (!row.hasCorrection || row.count === 0) {
      return;
    }

    const title = row.isDestructive ? 'Confirm deletion' : 'Apply correction';
    const message = row.isDestructive
      ? `This will permanently delete ${row.count} record(s) matching “${row.label}”. This action cannot be undone. Continue?`
      : `This will update ${row.count} record(s) matching “${row.label}”. Continue?`;
    const confirmText = row.isDestructive ? 'Delete' : 'Apply';
    const confirmIcon = row.isDestructive ? 'delete' : 'auto_fix_high';

    this.confirmationService
      .openConfirmationDialog(title, message, confirmText, 'Cancel', confirmIcon, 'clear')
      .subscribe((confirmed) => {
        if (!confirmed) return;

        row.applying = true;
        this.service.apply(row.checkType, row.isDestructive).subscribe({
          next: (result) => {
            row.applying = false;
            row.count = result.remainingCount;
            delete this.details[row.checkType];
            if (this.expandedCheck === row.checkType) {
              this.loadDetails(row.checkType, 1, 10);
            }
            this.toast.success(result.message);
          },
          error: () => {
            row.applying = false;
            this.toast.error('Error applying correction');
          }
        });
      });
  }

  /**
   * Deletes one reported record without leaving the list. The whole point of the button is to
   * spare the round trip through the Directors list, so the row is dropped from the page and
   * the check's count decremented in place rather than reloading the check.
   */
  deleteRow(check: CheckRow, result: AutomaticCorrectionResultDto): void {
    const rowId = result.directorId;
    if (!rowId || this.deletingRows.has(rowId)) return;

    this.confirmationService
      .openConfirmationDialog(
        'Confirm deletion',
        `Permanently delete “${result.name}”? This cannot be undone.`,
        'Delete',
        'Cancel',
        'delete',
        'clear'
      )
      .subscribe((confirmed) => {
        if (!confirmed) return;

        this.deletingRows.add(rowId);
        this.service.deleteRow(check.checkType, rowId).subscribe({
          next: (deletion) => {
            this.deletingRows.delete(rowId);
            this.removeRowFromDetails(check, rowId);
            check.count = Math.max(0, check.count - 1);
            this.toast.success(deletion.message);
          },
          error: (response) => {
            this.deletingRows.delete(rowId);
            // A 409 carries what still references the record; anything else is unexpected.
            const conflict = response?.error as AutomaticCorrectionRowDeletionResultDto | undefined;
            this.toast.error(conflict?.message ?? 'Error deleting the record');
          }
        });
      });
  }

  private removeRowFromDetails(check: CheckRow, rowId: string): void {
    const state = this.details[check.checkType];
    if (!state) return;

    this.details[check.checkType] = {
      ...state,
      results: state.results.filter(r => r.directorId !== rowId),
      totalCount: Math.max(0, state.totalCount - 1)
    };
  }

  countSeverity(count: number): 'none' | 'low' | 'medium' | 'high' {
    if (count === 0) return 'none';
    if (count < 10) return 'low';
    if (count < 100) return 'medium';
    return 'high';
  }

  trackByCheckType(_index: number, row: CheckRow): number {
    return row.checkType;
  }

  trackByRow(index: number, _row: AutomaticCorrectionResultDto): number {
    return index;
  }

  /**
   * Per-check query params for the company deep link. Defaults to none; checks
   * that operate on a specific tab override this so the user lands directly on
   * the relevant tab/year instead of the default Details tab.
   */
  companyLinkQueryParams(
    checkType: AutomaticCorrectionCheckType,
    row: AutomaticCorrectionResultDto
  ): Record<string, string> | null {
    if (checkType === AutomaticCorrectionCheckType.AuditCommitteeFinancialExpertNotFlagged) {
      return row.year ? { tab: 'committees', year: row.year.toString() } : { tab: 'committees' };
    }
    return null;
  }

  private getFilename(response: { headers: { get(name: string): string | null } }, fallbackPrefix: string): string {
    const disposition = response.headers.get('Content-Disposition');
    if (disposition) {
      const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(disposition);
      if (match?.[1]) {
        return decodeURIComponent(match[1]);
      }
    }
    return `${fallbackPrefix}_${new Date().toISOString().split('T')[0]}.csv`;
  }

  private downloadCsv(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
